import React, { useState, useEffect, useRef } from "react";
import { X, Download } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import Toast from "../../common/Toast/Toast";
import renderPdf from "../../common/RenderPdf/RenderPdf";
import EntityConstants from "../../configs/EntityConstants";
import { POST } from "../../service/CRUDService";

const CertificatePreviewModal = ({
  isOpen,
  onClose,
  previewUrl,
  printPreviewUrl,
  isLoading,
  credential_id,
}) => {
  const [currentView, setCurrentView] = useState("print");
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const pdfCanvasRef = useRef(null);
  const imgRef = useRef(null);

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  useEffect(() => {
    if (isOpen) {
      setCurrentView(printPreviewUrl ? "print" : "digital");
      setPdfLoaded(false);
      setImageLoaded(false);
    }
  }, [isOpen, printPreviewUrl]);

  useEffect(() => {
    if (isOpen && currentView === "print" && printPreviewUrl && !pdfLoaded) {
      console.log("Attempting to render PDF:", printPreviewUrl);

      const timer = setTimeout(() => {
        if (pdfCanvasRef.current) {
          console.log("Canvas found, rendering PDF...");
          renderPdf(printPreviewUrl, pdfCanvasRef)
            .then(() => {
              setPdfLoaded(true);
              console.log("PDF rendered successfully");
            })
            .catch((error) => {
              console.error("PDF rendering failed:", error);
            });
        } else {
          console.log("Canvas not found");
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentView, isOpen, printPreviewUrl, pdfLoaded]);

  useEffect(() => {
    if (currentView === "digital" && previewUrl) {
      setImageLoaded(false);
    }
  }, [currentView, previewUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(true);
  };

  const handleDownload = async () => {
    try {
      const presentationType = currentView === "digital" ? "pdf" : "legacy_pdf";

      const payload = {
        credential_id: credential_id,
        presentation_type: presentationType,
      };

      const response = await POST(
        EntityConstants.DOWNLOAD_CREDENTIALS,
        payload
      );

      if (!response.ok) throw new Error("Failed to fetch download URL");

      const data = await response.json();
      const { file_path: downloadUrl, file_name: filename } = data;

      const fileResp = await fetch(downloadUrl);
      if (!fileResp.ok) throw new Error("Failed to fetch file");

      const blob = await fileResp.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `${credential_id}.pdf`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to download file",
      });

      // Fallback - open in new tab
      window.open(
        currentView === "digital" ? previewUrl : printPreviewUrl,
        "_blank"
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Certificate Preview</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={
                isLoading ||
                !(currentView === "digital" ? previewUrl : printPreviewUrl)
              }
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium ${
                isLoading ||
                !(currentView === "digital" ? previewUrl : printPreviewUrl)
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-[#3538CD] hover:text-blue-700"
              } focus:outline-none`}
              title="Download certificate"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {previewUrl && printPreviewUrl && (
          <div className="border-b border-gray-200 mb-4 flex justify-center">
            <nav className="flex -mb-px">
              <button
                type="button"
                onClick={() => setCurrentView("digital")}
                className={`py-2 px-4 text-sm font-medium focus:outline-none ${
                  currentView === "digital"
                    ? "border-b-2 border-[#3538CD] text-[#3538CD]"
                    : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                } ${
                  !previewUrl ? "cursor-not-allowed opacity-50" : ""
                } transition-colors duration-200`}
                disabled={!previewUrl}
              >
                Digital View
              </button>
              <button
                type="button"
                onClick={() => {
                  if (printPreviewUrl) {
                    setCurrentView("print");
                    setPdfLoaded(false);
                  }
                }}
                className={`py-2 px-4 text-sm font-medium focus:outline-none ${
                  currentView === "print"
                    ? "border-b-2 border-[#3538CD] text-[#3538CD]"
                    : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                } ${
                  !printPreviewUrl ? "cursor-not-allowed opacity-50" : ""
                } transition-colors duration-200`}
                disabled={!printPreviewUrl}
              >
                Print View
              </button>
            </nav>
          </div>
        )}

        <div className="overflow-auto flex-1 p-4 flex items-center justify-center relative">
          {isLoading ? (
            <div className="text-gray-500 text-center">
              <svg
                className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
              Loading preview...
            </div>
          ) : currentView === "digital" ? (
            previewUrl ? (
              <div className="h-full w-full flex items-center justify-center relative">
                <img
                  ref={imgRef}
                  src={previewUrl}
                  className={`max-w-full max-h-[60vh] mx-auto object-contain ${
                    imageLoaded ? "opacity-100" : "opacity-0"
                  } transition-opacity duration-200`}
                  title="Certificate Preview"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  alt="Digital Certificate Preview"
                />
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="text-gray-500 text-center">
                      <svg
                        className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
                <p className="text-gray-500">Digital preview not available</p>
              </div>
            )
          ) : printPreviewUrl ? (
            <div className="h-full w-full flex items-center justify-center relative">
              <canvas
                ref={pdfCanvasRef}
                className={`max-w-full max-h-[60vh] ${
                  pdfLoaded ? "opacity-100" : "opacity-0"
                } transition-opacity duration-200`}
              />
              {!pdfLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                  <div className="text-gray-500 text-center">
                    <svg
                      className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
              <p className="text-gray-500">Print preview not available</p>
            </div>
          )}
        </div>

        <div className="flex justify-end p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificatePreviewModal;
